import React, { useState, useEffect, useMemo } from 'react';
import { 
  Search, 
  Users, 
  CheckCircle2, 
  XCircle, 
  Star,
  FileText
} from 'lucide-react';
import { FaEdit } from "react-icons/fa"; 
import { toast } from 'react-toastify';
import { useNavigate, useLocation } from 'react-router-dom'; 
import SummaryApi from '../common';

// --- Constants & Headers ---
const headers = {
  'Authorization': 'Bearer sandeep_university_916',
  'Content-Type': 'application/json',
};

const TeamLeadAllLeads = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // --- Data State ---
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // --- Filter State ---
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'interested'); 
  const [searchQuery, setSearchQuery] = useState('');
  const [districtFilter, setDistrictFilter] = useState("All");
  const [dateFilter, setDateFilter] = useState("All");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
const handleFirst = () => setCurrentPage(1);
const handleLast = () => setCurrentPage(totalPages);
  // --- Pagination State ---
  const [currentPage, setCurrentPage] = useState(1);
const [recordsPerPage, setRecordsPerPage] = useState(10);

  // --- 1. Fetch Dashboard Data ---
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const encodedEmployeeId = sessionStorage.getItem("H7Z4L9VD2XK5JW8QRYT6");
        
        if (!encodedEmployeeId) {
          const msg = "Employee ID not found. Please log in again.";
          toast.error(msg);
          setError(msg);
          setLoading(false);
          return;
        }

        const teamLeadEmpId = atob(encodedEmployeeId); 

        const response = await fetch(SummaryApi.tlDashboardCountData.url, {
          method: "POST", 
          headers: headers,
          body: JSON.stringify({
            team_lead_emp_id: teamLeadEmpId
          })
        });

        if (!response.ok) {
          throw new Error(`Network response was not ok: ${response.statusText}`);
        }

        const result = await response.json();

        if (result.status) {
          setData(result);
        } else {
          setError("Failed to load data.");
          toast.error("Failed to load data.");
        }
      } catch (err) {
        console.error("Fetch error:", err);
        setError("Something went wrong while fetching data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);
useEffect(() => {
  setCurrentPage(1);
}, [recordsPerPage]);
  // --- 2. Follow Up Logic (The Fix) ---
  const handleEditClick = async (student) => {
    try {
      // A. Fetch full student details first
      const responseDetails = await fetch(
        SummaryApi.getStudentDataByStudentId.url,
        {
          method: SummaryApi.getStudentDataByStudentId.method,
          headers: headers,
          body: JSON.stringify({ student_id: student.student_id }),
        }
      );
      
      const detailsResult = await responseDetails.json();
      const studentData = detailsResult.data || null;

      // B. Determine the correct Followup ID
      // Logic: If student.tl_student_followup_id is NULL (as you saw), 
      // we try to get it from the detailed 'studentData' we just fetched.
      const validFollowUpId = student.tl_student_followup_id || studentData?.tl_student_followup_id;

      // C. Fetch follow-up details ONLY if we found a valid ID
      let followUps = [];
      if (validFollowUpId) {
        const responseFollow = await fetch(
          SummaryApi.getStudentFollowDetails.url,
          {
            method: SummaryApi.getStudentFollowDetails.method,
            headers: headers,
            body: JSON.stringify({
              tl_student_followup_id: validFollowUpId,
            }),
          }
        );
        const followData = await responseFollow.json();
        followUps = followData.data || [];
      } else {
        console.log("No follow-up ID found (New Lead or No History)");
      }

      // D. Navigate to my-history page
      // IMPORTANT: We pass 'validFollowUpId' inside the student object so the next page has it
      navigate("/my-history", {
        state: {
          student: { ...student, tl_student_followup_id: validFollowUpId },
          studentDetails: studentData,
          studentFollowUps: followUps,
        },
      });
    } catch (err) {
      console.error("Error in handleEditClick:", err);
      toast.error("Something went wrong fetching details.");
    }
  };

  // --- 3. Helper: Date Filter Logic ---
  const filterByDateRange = (studentDate) => {
    if (!studentDate || studentDate === '0000-00-00') return true; 
    const date = new Date(studentDate);
    const today = new Date();
    
    const resetTime = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const dateNoTime = resetTime(date);
    const todayNoTime = resetTime(today);

    switch (dateFilter) {
      case "Today": return dateNoTime.getTime() === todayNoTime.getTime();
      case "Yesterday": {
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        return dateNoTime.getTime() === resetTime(yesterday).getTime();
      }
      case "This Week": {
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        return date >= weekStart && date <= today;
      }
      case "Last Week": {
        const lastWeekStart = new Date(today);
        lastWeekStart.setDate(today.getDate() - today.getDay() - 7);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
        return date >= lastWeekStart && date <= lastWeekEnd;
      }
      case "This Month": return date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
      case "Last Month": {
        const lastMonth = new Date(today);
        lastMonth.setMonth(today.getMonth() - 1);
        return date.getMonth() === lastMonth.getMonth() && date.getFullYear() === lastMonth.getFullYear();
      }
      case "Custom":
        if (startDate && endDate) {
          const s = new Date(startDate);
          const e = new Date(endDate);
          e.setHours(23, 59, 59, 999);
          return date >= s && date <= e;
        }
        return true;
      default: return true;
    }
  };

  // --- 4. Compute Unique Districts ---
  const districts = useMemo(() => {
    if (!data || !data.students) return ["All"];
    const allStudents = [
        ...(data.students.interested || []),
        ...(data.students.most_interested || [])
    ];
    const distSet = new Set(allStudents.map(s => s.district).filter(d => d && d !== 'N/A'));
    return ["All", ...Array.from(distSet)];
  }, [data]);

  // --- 5. Master Filter Logic ---
  const filteredList = useMemo(() => {
    if (!data || !data.students) return [];
    
    const list = data.students[activeTab] || [];
    
    return list.filter(student => {
      const query = searchQuery.toLowerCase();
      const matchesSearch = 
        searchQuery === "" ||
        (student.student_name && student.student_name.toLowerCase().includes(query)) ||
        (student.lead_name && student.lead_name.toLowerCase().includes(query)) ||
        (student.enrollment_code && student.enrollment_code.includes(query)) ||
        (student.mobile_number1 && student.mobile_number1.includes(query)) ||
        (student.city && student.city.toLowerCase().includes(query)) ||
        (student.clg_name && student.clg_name.toLowerCase().includes(query));

      const matchesDistrict = districtFilter === "All" || student.district === districtFilter;
      const matchesDate = filterByDateRange(student.created_date);

      return matchesSearch && matchesDistrict && matchesDate;
    });
  }, [data, activeTab, searchQuery, districtFilter, dateFilter, startDate, endDate]);

  // --- 6. Pagination Logic ---
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredList.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredList.length / recordsPerPage);

  const handlePrev = () => setCurrentPage((prev) => (prev > 1 ? prev - 1 : prev));
  const handleNext = () => setCurrentPage((prev) => (prev < totalPages ? prev + 1 : prev));

  // --- 7. Helper: Status Colors & Masking ---
  const getStatusColor = (status) => {
    const s = status ? status.toLowerCase() : '';
    if (s.includes('contacted')) return 'bg-green-100 text-green-800 border-green-200';
    if (s.includes('not answer')) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    if (s.includes('busy')) return 'bg-orange-100 text-orange-800 border-orange-200';
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const maskMobileNumber = (number) => {
    if (!number || number === 'N/A') return "-";
    return "**********"; 
  };

  // --- Loading & Error UI ---
  if (loading) return (
    <div className="flex justify-center items-center h-screen bg-gray-50">
      <div className="flex flex-col items-center gap-3">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <p className="text-gray-500 font-medium">Loading Dashboard...</p>
      </div>
    </div>
  );

  if (error && !data) return (
    <div className="flex justify-center items-center h-screen bg-gray-50">
      <div className="text-center p-8 bg-white shadow-lg rounded-xl border border-red-100">
        <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
        <h3 className="text-lg font-bold text-gray-900 mb-1">Error Loading Data</h3>
        <p className="text-red-500 font-medium">{error}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          Retry
        </button>
      </div>
    </div>
  );

  return (
    <div className="p-6 bg-gray-50 min-h-screen font-sans text-slate-800">
      
      {/* --- Header --- */}
      <div className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center">
        <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Team Lead Dashboard</h1>
            <p className="text-gray-500">Overview of all leads and recruitment statistics.</p>
        </div>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 md:mt-0 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg shadow-sm hover:bg-gray-100 transition"
        >
          Back
        </button>
      </div>

      {/* --- Statistics Cards --- */}
      {data?.counts && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard 
            title="Total Students" 
            count={data.counts.total_students || 0} 
            icon={<Users className="w-5 h-5 text-blue-600" />} 
            bg="bg-blue-50"
          />
          <StatCard 
            title="Most Interested" 
            count={data.counts.total_most_interested_count || 0} 
            icon={<Star className="w-5 h-5 text-yellow-600" />} 
            bg="bg-yellow-50"
          />
          <StatCard 
            title="Interested" 
            count={data.counts.total_interested_count || 0} 
            icon={<CheckCircle2 className="w-5 h-5 text-green-600" />} 
            bg="bg-green-50"
          />
          <StatCard 
            title="Not Interested" 
            count={data.counts.total_not_interested_count || 0} 
            icon={<XCircle className="w-5 h-5 text-red-600" />} 
            bg="bg-red-50"
          />
        </div>
      )}

      {/* --- Tab Navigation --- */}
      <div className="flex space-x-2 mb-6 overflow-x-auto pb-2">
        <button
          onClick={() => { setActiveTab('interested'); setCurrentPage(1); }}
          className={`px-4 py-2 rounded-lg border text-sm font-medium transition whitespace-nowrap ${
            activeTab === 'interested' 
              ? 'bg-blue-600 text-white border-blue-600 shadow-md' 
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
          }`}
        >
          Interested <span className="ml-1 bg-white/20 px-1.5 py-0.5 rounded text-xs">{data?.students?.interested?.length || 0}</span>
        </button>
        <button
          onClick={() => { setActiveTab('most_interested'); setCurrentPage(1); }}
          className={`px-4 py-2 rounded-lg border text-sm font-medium transition whitespace-nowrap ${
            activeTab === 'most_interested' 
              ? 'bg-blue-600 text-white border-blue-600 shadow-md' 
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
          }`}
        >
          Most Interested <span className="ml-1 bg-white/20 px-1.5 py-0.5 rounded text-xs">{data?.students?.most_interested?.length || 0}</span>
        </button>
      </div>

      {/* --- Unified Filter Toolbar --- */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="flex flex-col lg:flex-row gap-4 lg:items-center">
          
          {/* Search Bar */}
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search Name, ID, Mobile, City..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
            />
          </div>

          {/* Filters Group */}
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={districtFilter}
              onChange={(e) => {
                setDistrictFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[160px] cursor-pointer"
            >
              {districts.map((dist) => (
                <option key={dist} value={dist}>
                  {dist === "All" ? "Filter District" : dist}
                </option>
              ))}
            </select>

            <select
              value={dateFilter}
              onChange={(e) => {
                setDateFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[140px] cursor-pointer"
            >
              <option value="All">All Time</option>
              <option value="Today">Today</option>
              <option value="Yesterday">Yesterday</option>
              <option value="This Week">This Week</option>
              <option value="Last Week">Last Week</option>
              <option value="This Month">This Month</option>
              <option value="Last Month">Last Month</option>
              <option value="Custom">Custom Range</option>
            </select>

            {dateFilter === "Custom" && (
              <div className="flex items-center gap-2 animate-fadeIn">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="border border-gray-300 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-gray-400 font-medium">-</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="border border-gray-300 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* --- Data Table --- */}
      <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg shadow-sm">
        <table className="min-w-full text-sm text-left">
          <thead className="bg-gray-100 text-gray-700 font-semibold border-b border-gray-200">
            <tr>
              <th className="py-3 px-4 whitespace-nowrap">SNO</th>
              <th className="py-3 px-4 whitespace-nowrap">Student Name</th>
              <th className="py-3 px-4 whitespace-nowrap">District</th>
              <th className="py-3 px-4 whitespace-nowrap">City</th>
              <th className="py-3 px-4 whitespace-nowrap">Address</th>
              <th className="py-3 px-4 whitespace-nowrap">College Name</th>
              <th className="py-3 px-4 whitespace-nowrap">Mobile Number</th>
              <th className="py-3 px-4 whitespace-nowrap">Status</th>
              <th className="py-3 px-4 whitespace-nowrap">Remarks</th>
              <th className="py-3 px-4 whitespace-nowrap">Lead By</th>
              <th className="py-3 px-4 whitespace-nowrap">Action</th>
            </tr>
          </thead>
          <tbody>
            {currentRecords.length === 0 ? (
              <tr>
                <td colSpan="10" className="text-center py-10 text-gray-500">
                  <div className="flex flex-col items-center">
                    <FileText className="w-10 h-10 text-gray-300 mb-2" />
                    <p>No data found matching your criteria.</p>
                  </div>
                </td>
              </tr>
            ) : (
              currentRecords.map((student, index) => (
                <tr 
                  key={index} 
                  className={`border-b border-gray-100 transition duration-150 
                    ${
                      student.follow_up_date && student.follow_up_date !== '0000-00-00'
                        ? "bg-green-200 hover:bg-green-300" 
                        : index % 2 === 0 
                          ? "bg-white hover:bg-gray-50" 
                          : "bg-gray-50 hover:bg-gray-100"
                    }`}
                >
                  <td className="py-3 px-4 text-gray-500">{indexOfFirstRecord + index + 1}</td>
                  <td className="py-3 px-4 font-medium text-gray-900 whitespace-nowrap">
                    {student.student_name || "-"}
                    <div className="text-xs text-gray-400 font-normal mt-0.5">ID: {student.enrollment_code}</div>
                  </td>
                  <td className="py-3 px-4 whitespace-nowrap">{student.district || "-"}</td>
                  <td className="py-3 px-4 whitespace-nowrap">{student.city || "-"}</td>
                   <td className="py-3 px-4 whitespace-nowrap">{student.address || "-"}</td>
                  <td className="py-3 px-4 max-w-xs truncate" title={student.clg_name}>
                    {student.clg_name || "-"}
                  </td>
                  <td className="py-3 px-4 font-mono text-gray-600 whitespace-nowrap" title={student.mobile_number1}>
                    {student.mobile_number1 || "N/A"}
                  </td>
                  <td className="py-3 px-4 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(student.call_status)}`}>
                      {student.call_status || "Pending"}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-xs text-gray-500 max-w-xs whitespace-normal italic bg-white/50 p-1 rounded">
                      "{student.remarks && student.remarks !== 'N/A' ? student.remarks : 'No remarks'}"
                    </div>
                  </td>
                  <td className="py-3 px-4 whitespace-nowrap text-gray-500">
                    {student.lead_name || student.employee_name || '-'}
                  </td>
                  <td className="py-3 px-4 whitespace-nowrap">
                    <button
                        className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 text-xs font-medium transition shadow-sm flex items-center gap-1"
                        onClick={() => handleEditClick(student)}
                    >
                        <FaEdit /> Followup
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* --- Pagination --- */}
{filteredList.length > 0 && (
  <div className="flex items-center justify-between mt-6">

    {/* LEFT SIDE - Total Records */}
    <div className="text-sm font-medium text-gray-600">
      Total Records: <span className="font-semibold text-gray-900">{filteredList.length}</span>
    </div>

    {/* CENTER - Pagination */}
{/* CENTER - Pagination */}
<div className="flex items-center space-x-2">

  {/* First Button */}
  <button
    onClick={handleFirst}
    disabled={currentPage === 1}
    className={`px-3 py-1.5 border rounded-lg text-sm font-medium transition ${
      currentPage === 1
        ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
        : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
    }`}
  >
    First
  </button>

  {/* Previous Button */}
  <button
    onClick={handlePrev}
    disabled={currentPage === 1}
    className={`px-3 py-1.5 border rounded-lg text-sm font-medium transition ${
      currentPage === 1
        ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
        : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
    }`}
  >
    Previous
  </button>

  {/* Current Page Indicator */}
  <span className="px-4 py-1.5 border border-blue-600 rounded-lg bg-blue-600 text-white text-sm font-medium shadow-sm">
    {currentPage} / {totalPages || 1}
  </span>

  {/* Next Button */}
  <button
    onClick={handleNext}
    disabled={currentPage === totalPages || totalPages === 0}
    className={`px-3 py-1.5 border rounded-lg text-sm font-medium transition ${
      currentPage === totalPages || totalPages === 0
        ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
        : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
    }`}
  >
    Next
  </button>

  {/* Last Button */}
  <button
    onClick={handleLast}
    disabled={currentPage === totalPages || totalPages === 0}
    className={`px-3 py-1.5 border rounded-lg text-sm font-medium transition ${
      currentPage === totalPages || totalPages === 0
        ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
        : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
    }`}
  >
    Last
  </button>

</div>

    {/* RIGHT SIDE - Show Dropdown */}
{/* RIGHT SIDE - Show Dropdown */}
<div className="flex items-center gap-2">
  <span className="text-sm text-gray-600 font-medium">Show</span>
  <select
    value={recordsPerPage === filteredList.length ? "all" : recordsPerPage}
    onChange={(e) => {
      const val = e.target.value;
      if (val === "all") {
        setRecordsPerPage(filteredList.length);
      } else {
        setRecordsPerPage(Number(val));
      }
      setCurrentPage(1);
    }}
    className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
  >
    {/* Base options */}
    <option value={10}>10</option>
    <option value={100}>100</option>
    <option value={300}>300</option>
    <option value={500}>500</option>
    
    {/* Generate options in increments of 200 after 500 */}
    {filteredList.length > 500 && (
      Array.from(
        { length: Math.floor((filteredList.length - 500) / 200) + 1 },
        (_, i) => {
          const value = 500 + ((i + 1) * 200);
          // Only show if value is less than or equal to total records
          // and don't show if it exceeds a reasonable limit (optional)
          if (value <= filteredList.length && value <= 5000) {
            return (
              <option key={value} value={value}>
                {value}
              </option>
            );
          }
          return null;
        }
      )
    )}
    
    {/* Always show "All" option with total count */}
    <option value="all" className="font-medium text-blue-600">
      All ({filteredList.length})
    </option>
    
  </select>
</div>

  </div>
)}
    </div>
  );
};

// --- StatCard Component ---
const StatCard = ({ title, count, icon, bg }) => (
  <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200 flex items-center justify-between hover:shadow-md transition-shadow cursor-default">
    <div>
      <p className="text-sm font-medium text-gray-500 mb-1">{title}</p>
      <h3 className="text-2xl font-bold text-gray-900 tracking-tight">{count}</h3>
    </div>
    <div className={`p-3 rounded-full ${bg}`}>
      {icon}
    </div>
  </div>
);

export default TeamLeadAllLeads;